import React, { useState, useEffect } from 'react';
import { useCalculator } from '../context/CalculatorContext';
import { formatCurrency } from '../utils/formatters';
import { WhatIfScenarioParams, ScenarioType } from '../types';
import { 
  TrendingUp, 
  DollarSign, 
  Percent, 
  ChevronRight, 
  ChevronLeft, 
  AlertTriangle, 
  Zap, 
  Flame, 
  CloudLightning, 
  ArrowUp, 
  Thermometer, 
  Settings 
} from 'lucide-react';
import { motion } from 'framer-motion';
import Slider from './ui/Slider';

interface WhatIfScenarioConfigProps {
  onNext: () => void;
  onBack: () => void;
}

const WhatIfScenarioConfig: React.FC<WhatIfScenarioConfigProps> = ({ onNext, onBack }) => {
  const { 
    investment, 
    whatIfScenario, 
    setWhatIfScenario, 
    clearWhatIfScenario,
    applyPredefinedScenario
  } = useCalculator();

  // Initialize scenario params with current values or defaults
  const [scenarioParams, setScenarioParams] = useState<WhatIfScenarioParams>({
    name: whatIfScenario?.name || "Escenario Personalizado",
    description: whatIfScenario?.description || "Parámetros personalizados definidos por el usuario",
    averageProductionPerHectare: whatIfScenario?.averageProductionPerHectare || investment.averageProductionPerHectare,
    averageSalePricePerKg: whatIfScenario?.averageSalePricePerKg || investment.averageSalePricePerKg,
    lemonPriceIncrease: whatIfScenario?.lemonPriceIncrease || investment.lemonPriceIncrease,
    inflationRate: whatIfScenario?.inflationRate || investment.inflationRate,
    cetesRate: whatIfScenario?.cetesRate || investment.cetesRate,
    savingsRate: whatIfScenario?.savingsRate || investment.savingsRate,
    realEstateAppreciation: whatIfScenario?.realEstateAppreciation || investment.realEstateAppreciation,
    realEstateRent: whatIfScenario?.realEstateRent || investment.realEstateRent,
    ebitdaFactor: whatIfScenario?.ebitdaFactor || investment.ebitdaFactor
  });

  // Update scenario params when whatIfScenario changes
  useEffect(() => {
    if (whatIfScenario) {
      setScenarioParams({
        name: whatIfScenario.name,
        description: whatIfScenario.description,
        averageProductionPerHectare: whatIfScenario.averageProductionPerHectare || investment.averageProductionPerHectare,
        averageSalePricePerKg: whatIfScenario.averageSalePricePerKg || investment.averageSalePricePerKg,
        lemonPriceIncrease: whatIfScenario.lemonPriceIncrease || investment.lemonPriceIncrease,
        inflationRate: whatIfScenario.inflationRate || investment.inflationRate,
        cetesRate: whatIfScenario.cetesRate || investment.cetesRate,
        savingsRate: whatIfScenario.savingsRate || investment.savingsRate,
        realEstateAppreciation: whatIfScenario.realEstateAppreciation || investment.realEstateAppreciation,
        realEstateRent: whatIfScenario.realEstateRent || investment.realEstateRent,
        ebitdaFactor: whatIfScenario.ebitdaFactor || investment.ebitdaFactor
      });
    }
  }, [whatIfScenario, investment]);

  // Update a specific parameter
  const updateParam = (param: keyof WhatIfScenarioParams, value: any) => {
    setScenarioParams(prev => ({
      ...prev,
      [param]: value
    }));
  };

  // Apply the current scenario
  const applyScenario = () => {
    setWhatIfScenario(scenarioParams);
    onNext(); // Call onNext immediately to navigate to comparison view
  };

  // Reset to base values
  const resetScenario = () => {
    clearWhatIfScenario();
    setScenarioParams({
      name: "Escenario Personalizado",
      description: "Parámetros personalizados definidos por el usuario",
      averageProductionPerHectare: investment.averageProductionPerHectare,
      averageSalePricePerKg: investment.averageSalePricePerKg,
      lemonPriceIncrease: investment.lemonPriceIncrease,
      inflationRate: investment.inflationRate,
      cetesRate: investment.cetesRate,
      savingsRate: investment.savingsRate,
      realEstateAppreciation: investment.realEstateAppreciation,
      realEstateRent: investment.realEstateRent,
      ebitdaFactor: investment.ebitdaFactor
    });
  };

  // Apply a predefined scenario
  const handleApplyPredefinedScenario = (scenarioType: ScenarioType) => {
    applyPredefinedScenario(scenarioType);
    onNext(); // Call onNext immediately to navigate to comparison view
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-5xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 to-red-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Modo "¿Qué pasaría si...?"</h2>
              <p className="text-amber-100">Experimenta con escenarios extremos para evaluar la resiliencia de tu inversión</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          {/* Predefined Scenarios */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <Flame className="w-5 h-5 text-amber-600" />
              <span>Escenarios Predefinidos</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => handleApplyPredefinedScenario('optimistic')}
                className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl hover:shadow-md transition-all duration-300 text-left"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-200 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-700" />
                  </div>
                  <h4 className="font-medium text-green-800">Escenario Optimista</h4>
                </div>
                <p className="text-sm text-green-600">Condiciones de mercado favorables con alto crecimiento y baja inflación</p>
              </button>
              
              <button
                onClick={() => handleApplyPredefinedScenario('pessimistic')}
                className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl hover:shadow-md transition-all duration-300 text-left"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-orange-200 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-orange-700 transform rotate-180" />
                  </div>
                  <h4 className="font-medium text-orange-800">Escenario Pesimista</h4>
                </div>
                <p className="text-sm text-orange-600">Condiciones de mercado desfavorables con bajo crecimiento y alta inflación</p>
              </button>
              
              <button
                onClick={() => handleApplyPredefinedScenario('crisis')}
                className="p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-xl hover:shadow-md transition-all duration-300 text-left"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-red-200 rounded-lg flex items-center justify-center">
                    <CloudLightning className="w-5 h-5 text-red-700" />
                  </div>
                  <h4 className="font-medium text-red-800">Crisis Económica</h4>
                </div>
                <p className="text-sm text-red-600">Escenario de crisis económica severa con alta inflación y rendimientos reducidos</p>
              </button>
              
              <button
                onClick={() => handleApplyPredefinedScenario('boom')}
                className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl hover:shadow-md transition-all duration-300 text-left"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-blue-700" />
                  </div>
                  <h4 className="font-medium text-blue-800">Boom Económico</h4>
                </div>
                <p className="text-sm text-blue-600">Escenario de auge económico con crecimiento excepcional y baja inflación</p>
              </button>
              
              <button
                onClick={() => handleApplyPredefinedScenario('inflation')}
                className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl hover:shadow-md transition-all duration-300 text-left"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-200 rounded-lg flex items-center justify-center">
                    <Thermometer className="w-5 h-5 text-purple-700" />
                  </div>
                  <h4 className="font-medium text-purple-800">Inflación Alta</h4>
                </div>
                <p className="text-sm text-purple-600">Escenario de inflación elevada sostenida con ajustes en tasas de interés</p>
              </button>
            </div>
          </div>
          
          {/* Custom Scenario Configuration */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-600" />
              <span>Configuración de Escenario Personalizado</span>
            </h3>
            
            {/* Scenario Name and Description */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Nombre del Escenario</label>
                <input
                  type="text"
                  value={scenarioParams.name}
                  onChange={(e) => updateParam('name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Ej: Mi Escenario Personalizado"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Descripción</label>
                <input
                  type="text"
                  value={scenarioParams.description}
                  onChange={(e) => updateParam('description', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Breve descripción del escenario"
                />
              </div>
            </div>
            
            {/* Cosecha Capital Parameters */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-800">Parámetros de Cosecha Capital</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Producción por Hectárea</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="15000"
                      max="35000"
                      step="500"
                      value={scenarioParams.averageProductionPerHectare || investment.averageProductionPerHectare}
                      onChange={(e) => updateParam('averageProductionPerHectare', parseInt(e.target.value))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                    <span className="text-gray-700 text-sm">kg/ha</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Precio por Kg</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="20"
                      max="50"
                      step="1"
                      value={scenarioParams.averageSalePricePerKg || investment.averageSalePricePerKg}
                      onChange={(e) => updateParam('averageSalePricePerKg', parseInt(e.target.value))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                    <span className="text-gray-700 text-sm">MXN/kg</span>
                  </div>
                </div>
              </div>
              
              {/* Calculated Annual Profit Display */}
              <div className="p-3 bg-amber-50 rounded-lg">
                <div className="text-sm text-amber-700 mb-1">Utilidad anual calculada:</div>
                <div className="text-lg font-bold text-amber-800">
                  {(((scenarioParams.averageProductionPerHectare || investment.averageProductionPerHectare) * 
                     (scenarioParams.averageSalePricePerKg || investment.averageSalePricePerKg) * 0.1 * 0.65 / 
                     investment.certificateBasePrice) * 100).toFixed(1)}%
                </div>
              </div>
              
              <Slider
                label="Incremento Precio Limón"
                value={scenarioParams.lemonPriceIncrease || investment.lemonPriceIncrease}
                min={0}
                max={15}
                step={0.5}
                onChange={(value) => updateParam('lemonPriceIncrease', value)}
                formatter={(value) => `${value}%`}
                tooltip="Incremento anual del precio del limón"
              />
              
              <h4 className="font-medium text-gray-800">Parámetros Económicos</h4>
              
              <Slider
                label="Tasa de Inflación"
                value={scenarioParams.inflationRate || investment.inflationRate}
                min={0}
                max={20}
                step={0.5}
                onChange={(value) => updateParam('inflationRate', value)}
                formatter={(value) => `${value}%`}
                tooltip="Tasa de inflación anual"
              />
            </div>
            
            {/* Comparative Investment Parameters */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-800">Parámetros de Inversiones Comparativas</h4>
              
              <Slider
                label="CETES"
                value={scenarioParams.cetesRate || investment.cetesRate}
                min={1}
                max={20}
                step={0.5}
                onChange={(value) => updateParam('cetesRate', value)}
                formatter={(value) => `${value}%`}
                tooltip="Rendimiento anual de CETES"
              />
              
              <Slider
                label="Ahorro Tradicional"
                value={scenarioParams.savingsRate || investment.savingsRate}
                min={1}
                max={15}
                step={0.5}
                onChange={(value) => updateParam('savingsRate', value)}
                formatter={(value) => `${value}%`}
                tooltip="Rendimiento anual de ahorro tradicional"
              />
              
              <Slider
                label="Bienes Raíces - Apreciación"
                value={scenarioParams.realEstateAppreciation || investment.realEstateAppreciation}
                min={0}
                max={20}
                step={0.5}
                onChange={(value) => updateParam('realEstateAppreciation', value)}
                formatter={(value) => `${value}%`}
                tooltip="Apreciación anual de bienes raíces"
              />
              
              <Slider
                label="Bienes Raíces - Renta"
                value={scenarioParams.realEstateRent || investment.realEstateRent}
                min={0}
                max={15}
                step={0.5}
                onChange={(value) => updateParam('realEstateRent', value)}
                formatter={(value) => `${value}%`}
                tooltip="Rendimiento anual por renta de bienes raíces"
              />
            </div>
            
            {/* Scenario Warning */}
            <div className="bg-amber-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-700" />
                </div>
                <h4 className="font-medium text-amber-800">Advertencia sobre Escenarios Extremos</h4>
              </div>
              <p className="text-sm text-amber-700">
                Los escenarios extremos son útiles para evaluar la resiliencia de tu inversión, pero representan situaciones atípicas. 
                Los resultados mostrados son proyecciones basadas en los parámetros especificados y no deben considerarse como garantías 
                de rendimiento futuro.
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-between">
            <div className="space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Volver</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={resetScenario}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
              >
                <Settings className="w-5 h-5" />
                <span>Restablecer</span>
              </motion.button>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={applyScenario}
              className="px-8 py-3 bg-gradient-to-r from-amber-500 to-red-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>Aplicar Escenario</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WhatIfScenarioConfig;